import { Close as CloseIcon, CloudUpload as UploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Paper,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useGallery from '../../../../hooks/useGallery';

const GalleryManagement = () => {
  const { photos, isGalleryLoading, addPhotos, isAddingPhotos, deletePhoto, isDeletingPhoto } = useGallery();

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [deletePhotoId, setDeletePhotoId] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setSelectedFiles(files);
    setPreviews(files.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })));
  };

  const handleRemovePreview = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => {
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append('photos', file));
      await addPhotos(formData);
      toast.success(`${selectedFiles.length} photo(s) uploaded successfully`);
      setSelectedFiles([]);
      setPreviews([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Upload failed');
    }
  };

  const handleDelete = async () => {
    try {
      await deletePhoto(deletePhotoId);
      toast.success('Photo deleted');
      setDeletePhotoId(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gallery
        </Typography>
        <Button
          variant="contained"
          sx={{ background: '#454751d1', fontSize: 15 }}
          startIcon={<UploadIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload Photos
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>

      {/* Selected files preview */}
      {previews.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography fontSize={14} fontWeight={600} mb={1.5}>
            Selected ({previews.length}) — ready to upload
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            {previews.map((p, i) => (
              <Box
                key={i}
                sx={{
                  position: 'relative',
                  width: 100,
                  height: 100,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <img
                  src={p.url}
                  alt={p.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemovePreview(i)}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    p: '2px',
                    '&:hover': { background: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              sx={{ background: '#454751d1', fontSize: 14 }}
              onClick={handleUpload}
              disabled={isAddingPhotos}
              startIcon={isAddingPhotos ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            >
              {isAddingPhotos ? 'Uploading...' : `Upload ${previews.length} Photo(s)`}
            </Button>
            <Button
              variant="outlined"
              sx={{ fontSize: 14 }}
              onClick={() => {
                previews.forEach((p) => URL.revokeObjectURL(p.url));
                setSelectedFiles([]);
                setPreviews([]);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              Clear
            </Button>
          </Box>
        </Paper>
      )}

      {/* Gallery grid */}
      {isGalleryLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : photos.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No photos in the gallery yet. Upload some to get started.
          </Typography>
        </Paper>
      ) : (
        <ImageList cols={4} gap={12} sx={{ mt: 0 }}>
          {photos.map((photo) => (
            <ImageListItem
              key={photo._id}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                '& img': { display: 'block' },
              }}
            >
              <img
                src={photo.url}
                alt={photo.name}
                loading="lazy"
                style={{ width: '100%', height: 180, objectFit: 'cover' }}
              />
              <ImageListItemBar
                title={
                  <Typography fontSize={12} noWrap>
                    {photo.name || 'Gallery photo'}
                  </Typography>
                }
                actionIcon={
                  <IconButton
                    size="small"
                    sx={{ color: '#ff5252', mr: 0.5 }}
                    onClick={() => setDeletePhotoId(photo._id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
                sx={{ background: 'rgba(0,0,0,0.55)' }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deletePhotoId} onClose={() => setDeletePhotoId(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Delete Photo
            <IconButton edge="end" color="inherit" onClick={() => setDeletePhotoId(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Typography fontSize={14}>Are you sure you want to remove this photo from the gallery?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePhotoId(null)} sx={{ fontSize: 13, color: '#454751d1' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={isDeletingPhoto}
            sx={{ fontSize: 13 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GalleryManagement;
